import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Spacings } from '@styles'

import { DrawerBodyWrapper } from '@base'
import { CRAN_SECTION_TYPES } from '@constants'

import { showLoader } from '@data/state/action'
import CranCommentCard from '../Components/CranCommentCard'
import { addCommentInCranReport } from '@data/conn/post/cranReport'
import { useForceUpdate } from '@hooks'
import { getCranReportById } from '@data/conn/get/cranReport'
import { getLatestCommentImagePaths } from '../../../cranCommentImages'

// Grey body so the white comment cards stand out against it.
const DrawerBody = styled(DrawerBodyWrapper)`
	background: ${({ theme }) => theme.colors.surface.page};
`

// Each comment sits in its own card, spaced apart.
const FieldWrapper = styled.div`
	& + & {
		margin-top: ${Spacings.SPACING_3B};
	}
`

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		// Documents of the same (last) entry the comment itself comes from.
		documents: getLatestCommentImagePaths(data),
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const _state = {
		overall_comment: {
			label: 'Overall Comment',
			placeholder: 'Enter overall comment here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		management_risk: {
			label: 'Management Risk',
			placeholder: 'Enter comments on management risk here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		management_quality: {
			label: 'Management Quality',
			placeholder: 'Enter comments on quality of Quality here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		financial_policies: {
			label: 'Financial Policies',
			placeholder: 'Enter comments on financial policies here.',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		succession_planning: {
			label: 'Succession Planning',
			placeholder: 'Enter comments on succession planning here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		governance_and_ethics: {
			label: 'Governance and Ethics',
			placeholder: 'Enter comments on governance and ethics here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}

	if (data) {
		_state.overall_comment = dataToForm(_state.overall_comment, data?.overall_comment)
		_state.management_risk = dataToForm(_state.management_risk, data?.management_risk)
		_state.management_quality = dataToForm(_state.management_quality, data?.management_quality)
		_state.financial_policies = dataToForm(_state.financial_policies, data?.financial_policies)
		_state.succession_planning = dataToForm(_state.succession_planning, data?.succession_planning)
		_state.governance_and_ethics = dataToForm(_state.governance_and_ethics, data?.governance_and_ethics)
	}
	return _state
}

const ManagementRIskForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.MANAGEMENT])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	// Every change to a comment — its text, its CRAN visibility or its sentiment — is saved with
	// the same endpoint, so the card never holds an unsaved state.
	const saveComment = (key, overrides = {}) => {
		const obj = { ...formState[key], ...overrides }
		const comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
		saveComment(key, { show_in_cran: val })
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
		saveComment(key, { comment_type: value })
	}

	// `value` is the draft the editor just saved; it is also written back to the form so the card
	// shows the new comment immediately, before the refetch lands.
	const addHandler = (key) => (value) => {
		const comment = value ?? formState[key]?.value
		updateState(key)(comment)
		saveComment(key, { value: comment })
	}

	return (
		<>
			<DrawerBody>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.overall_comment?.label}
						placeholder={formState?.overall_comment?.placeholder}
						toggleState={formState?.overall_comment?.comment_type}
						savedDocuments={formState?.overall_comment?.documents}
						textValue={formState?.overall_comment?.value}
						onCheckHandler={updateCheckHandler('overall_comment')}
						checkValue={formState?.overall_comment?.show_in_cran}
						onToggleChange={commentTypeHandler('overall_comment')}
						onButtonClick={addHandler('overall_comment')}
						disabled={formState?.overall_comment?.disabled}
						probeId={probeId}
						commentKey={'overall_comment'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.management_risk?.label}
						placeholder={formState?.management_risk?.placeholder}
						toggleState={formState?.management_risk?.comment_type}
						savedDocuments={formState?.management_risk?.documents}
						textValue={formState?.management_risk?.value}
						onCheckHandler={updateCheckHandler('management_risk')}
						checkValue={formState?.management_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('management_risk')}
						onButtonClick={addHandler('management_risk')}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.management_quality?.label}
						placeholder={formState?.management_quality?.placeholder}
						toggleState={formState?.management_quality?.comment_type}
						savedDocuments={formState?.management_quality?.documents}
						textValue={formState?.management_quality?.value}
						onCheckHandler={updateCheckHandler('management_quality')}
						checkValue={formState?.management_quality?.show_in_cran}
						onToggleChange={commentTypeHandler('management_quality')}
						onButtonClick={addHandler('management_quality')}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.financial_policies?.label}
						placeholder={formState?.financial_policies?.placeholder}
						toggleState={formState?.financial_policies?.comment_type}
						savedDocuments={formState?.financial_policies?.documents}
						textValue={formState?.financial_policies?.value}
						onCheckHandler={updateCheckHandler('financial_policies')}
						checkValue={formState?.financial_policies?.show_in_cran}
						onToggleChange={commentTypeHandler('financial_policies')}
						onButtonClick={addHandler('financial_policies')}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.succession_planning?.label}
						placeholder={formState?.succession_planning?.placeholder}
						toggleState={formState?.succession_planning?.comment_type}
						savedDocuments={formState?.succession_planning?.documents}
						textValue={formState?.succession_planning?.value}
						onCheckHandler={updateCheckHandler('succession_planning')}
						checkValue={formState?.succession_planning?.show_in_cran}
						onToggleChange={commentTypeHandler('succession_planning')}
						onButtonClick={addHandler('succession_planning')}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.governance_and_ethics?.label}
						placeholder={formState?.governance_and_ethics?.placeholder}
						toggleState={formState?.governance_and_ethics?.comment_type}
						savedDocuments={formState?.governance_and_ethics?.documents}
						textValue={formState?.governance_and_ethics?.value}
						onCheckHandler={updateCheckHandler('governance_and_ethics')}
						checkValue={formState?.governance_and_ethics?.show_in_cran}
						onToggleChange={commentTypeHandler('governance_and_ethics')}
						onButtonClick={addHandler('governance_and_ethics')}
					/>
				</FieldWrapper>
			</DrawerBody>

			{/* <DrawerFooterWrapper>
				<Button small onClick={submitHandler}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper> */}
		</>
	)
}

export default ManagementRIskForm
